type File = {
  file: string;
  width: number;
  height: number;
};

/**
 * Retrieves a file URL from a list of files based on specified dimensions.
 * @param files - Array of files with URL and dimension properties.
 * @param width - Desired minimum width. Defaults to Infinity.
 * @param height - Desired minimum height. Defaults to Infinity.
 * @returns The URL of the best-matching file or an empty string if no files are found.
 */
export const getFileUrlByDimensions = (files: File[], width: number = Infinity, height: number = Infinity): string => {
  if (!files?.length) {
    return '';
  }

  const sortedBySize = files.slice().sort((a, b) => a.height - b.height);
  let matchedFile = sortedBySize[sortedBySize.length - 1];

  if (width !== Infinity && height !== Infinity) {
    const file = sortedBySize.find((item) => item.height >= height && item.width >= width);
    if (file) {
      matchedFile = file;
    }
  }

  return matchedFile.file;
};

/**
 * Downloads a file from the specified URL.
 * @param url - The URL of the file to download.
 * @param fileName - The name to save the downloaded file as.
 * @param callbackFn - Optional callback function, invoked with `true` if download succeeds or `false` if it fails.
 */
export const downloadFile = (
  url: string,
  fileName: string | undefined,
  callbackFn: (success: boolean) => void = () => null,
): void => {
  fetch(url, {
    headers: new Headers({
      Origin: window.location.origin,
    }),
    mode: 'cors',
  })
    .then((response) => response.blob())
    .then((blob) => {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', fileName ?? (url.split('/').pop() || 'download'));

      link.click();

      callbackFn(true);
    })
    .catch(() => {
      callbackFn(false);
    });
};
